import { useState } from 'react';
import type { ExportDestinationContent, ColumnFormatSpec } from '@capture/shared';
import { normalizeColumnFormat } from '@capture/shared';
import { FormatSpecEditor } from './FormatSpecEditor.js';
import styles from './export-composer.module.css';

type Mapping = ExportDestinationContent['mappings'][number];

/**
 * Per-mapping-row format override editor — EX-2 / F-053 / Task 14.
 *
 * Replaces the legacy date/number-direct-controls with an inherit/override
 * model:
 *   - Shows the column's inherited ColumnFormatSpec as a read-only summary.
 *   - Shows the column's schema note read-only.
 *   - "Override for this form" toggle: OFF → inherits (no formatOverride
 *     stored); ON → opens FormatSpecEditor pre-seeded with the inherited
 *     spec and emits { formatOverride: inheritedSpec }.
 *   - Soft guard: when the column has a note and the operator flips the
 *     toggle ON, shows a non-blocking inline acknowledgment before the
 *     spec editor.
 *
 * Controlled component: receives the current mapping, emits patches via
 * `onChange`.
 */

function describeSpec(spec: ColumnFormatSpec): string {
  switch (spec.kind) {
    case 'raw':
      return 'raw (no formatting)';
    case 'date':
      return `date · ${spec.token}`;
    case 'number': {
      const parts = ['number'];
      if (spec.decimals != null) parts.push(`${spec.decimals} dp`);
      if (spec.currencySymbol) parts.push(spec.currencySymbol);
      return parts.join(' · ');
    }
    case 'text': {
      const parts = ['text'];
      if (spec.maxLength != null) parts.push(`max ${spec.maxLength}`);
      if (spec.case) parts.push(spec.case === 'upper' ? 'UPPERCASE' : spec.case);
      return parts.join(' · ');
    }
  }
}

interface MappingRowFormatEditorProps {
  mapping: Mapping;
  column: { format?: ColumnFormatSpec; dataType?: string; note?: string };
  onChange: (patch: Partial<Mapping>) => void;
  /** Index of the mapping in its containing list — used for unique form ids. */
  rowIndex: number;
}

export function MappingRowFormatEditor({ mapping, column, onChange, rowIndex }: MappingRowFormatEditorProps) {
  const inherited = normalizeColumnFormat(column);
  // Purely controlled: isOverriding is derived from the prop alone.
  // The parent owns mapping.formatOverride; toggling emits a patch and the
  // parent re-renders with the new value.
  const isOverriding = mapping.formatOverride != null;
  // Ephemeral soft-guard state — resets when the toggle goes off.
  const [ackNeeded, setAckNeeded] = useState(false);

  function toggleOverride(next: boolean) {
    if (next) {
      setAckNeeded(column.note ? true : false);
      onChange({ formatOverride: inherited });
    } else {
      setAckNeeded(false);
      onChange({ formatOverride: undefined });
    }
  }

  // The active spec to display in the editor: prefer the controlled prop's
  // override (reflects any FormatSpecEditor changes), fall back to inherited.
  const activeSpec = mapping.formatOverride ?? inherited;

  return (
    <div className={styles.mappingFormatEditor} aria-label="Per-row format overrides">
      <div className={styles.mappingFormatGroup}>
        <span className={styles.mappingFormatLabel}>inherits: {describeSpec(inherited)}</span>
        {column.note && (
          <p className="rb-help" data-testid={`note-${rowIndex}`}>
            Receiver note: {column.note}
          </p>
        )}
        <label>
          <input
            type="checkbox"
            aria-label="Override for this form"
            checked={isOverriding}
            onChange={(e) => toggleOverride(e.target.checked)}
          />{' '}
          Override for this form
        </label>
      </div>

      {isOverriding && ackNeeded && column.note && (
        <p className="dialog-field-error" role="alert">
          Receiver note: &ldquo;{column.note}&rdquo;. Override anyway?
        </p>
      )}

      {isOverriding && (
        <FormatSpecEditor
          spec={activeSpec}
          onChange={(next) => onChange({ formatOverride: next })}
          idPrefix={`row-${rowIndex}`}
        />
      )}
    </div>
  );
}
